<script setup lang="ts">
  import { Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { useCloseOnEscape } from '@/entities/searchForm';
  import MorphPopoverLayer from '@/shared/ui/MorphPopoverLayer.vue';
  import NightsStepper from './NightsStepper.vue';
  import { NIGHTS_RANGE_TEXTS } from '../config/texts';

  interface NightsRangeDropdownProps {
    from: number;
    to: number;
    visible: boolean;
    summary: string;
    dropdownTarget?: HTMLElement | null;
  }

  const props = withDefaults(defineProps<NightsRangeDropdownProps>(), {
    dropdownTarget: null,
  });

  interface NightsRangeDropdownEmits {
    (e: 'update:from', v: number): void;
    (e: 'update:to', v: number): void;
    (e: 'close'): void;
  }

  const emit = defineEmits<NightsRangeDropdownEmits>();

  useCloseOnEscape(
    () => props.visible,
    () => emit('close'),
  );
</script>

<template>
  <MorphPopoverLayer
    :target="dropdownTarget"
    :visible="visible"
  >
    <div
      class="nights-range-dropdown"
      data-testid="nights-range-dropdown"
    >
      <div class="nights-range-dropdown__steppers">
        <div class="nights-range-dropdown__stepper">
          <Typo variant="body-l-400">От</Typo>
          <NightsStepper
            :label="NIGHTS_RANGE_TEXTS.fromLabel"
            :value="from"
            testid="nights-range-from"
            @update:value="emit('update:from', $event)"
          />
        </div>
        <div class="nights-range-dropdown__stepper">
          <Typo variant="body-l-400">До</Typo>
          <NightsStepper
            :label="NIGHTS_RANGE_TEXTS.toLabel"
            :value="to"
            testid="nights-range-to"
            @update:value="emit('update:to', $event)"
          />
        </div>
      </div>

      <div
        class="nights-range-dropdown__summary"
        data-testid="nights-range-summary"
      >
        <Typo
          :variant="TypoVariant.BodyLRegular"
          tag="span"
        >
          {{ summary }}
        </Typo>
      </div>
    </div>
  </MorphPopoverLayer>
</template>

<style scoped lang="scss">
  .nights-range-dropdown {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xl);
    min-width: 264px;
    padding: var(--spacing-l) var(--spacing-m);
    width: max-content;

    &__stepper {
      align-items: center;
      display: flex;
      gap: var(--spacing-s);
    }

    &__steppers {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-l);
    }

    &__summary {
      color: var(--color-text-primary);
    }
  }
</style>
